import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { getRecipe, parseWeight } from '@recipe/shared';
import { ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import './CookingMode.css';

const CookingMode = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlServings = searchParams.get('servings');
  const initialServings = Number(urlServings) || Number(location.state?.desiredServings) || null;

  const [recipe, setRecipe] = useState(null);
  const [desiredServings, setDesiredServings] = useState(initialServings);
  const [loading, setLoading] = useState(true);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Flatten all steps for easy navigation
  const [allSteps, setAllSteps] = useState([]);

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const data = await getRecipe(id);
        if (data) {
          setRecipe(data);
          if (!initialServings && data.servings) {
            setDesiredServings(data.servings);
          }
          
          const steps = [];
          data.parts?.forEach(part => {
            part.steps?.forEach((step, index) => {
              steps.push({
                partName: part.name,
                stepNumber: index + 1,
                ...step
              });
            });
          });
          setAllSteps(steps);
        }
      } catch (error) {
        console.error('Error fetching recipe:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [id, initialServings]);

  const handleNext = () => {
    if (currentStepIndex < allSteps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className="cooking-mode-loading">
        <Loader2 size={48} className="spin" color="#ff6b6b" />
      </div>
    );
  }

  if (!recipe || allSteps.length === 0) {
    return <div className="cooking-mode-error">食谱加载失败或没有步骤</div>;
  }

  const getAdjustedWeightDisplay = (rawWeightStr) => {
    if (!rawWeightStr) return '';
    const { number, unit, raw } = parseWeight(rawWeightStr);
    if (number === 0 && raw) return raw;
    const originalServings = recipe?.servings || 1;
    const currentServings = desiredServings || originalServings;
    const ratio = currentServings / originalServings;
    const adjusted = Math.round(number * ratio * 10) / 10;
    return `${adjusted}${unit}`;
  };

  const currentStep = allSteps[currentStepIndex];
  const progress = ((currentStepIndex + 1) / allSteps.length) * 100;

  return (
    <div className="cooking-mode-container">
      <div className="cooking-header">
        <button className="close-btn" onClick={() => navigate(-1)}>
          <X size={32} />
        </button>
        <div className="cooking-title-area">
          <h2>{recipe.title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span className="cooking-part-name">
              {currentStep.partName ? `${currentStep.partName} - ` : ''} 步骤 {currentStep.stepNumber}
            </span>
            {desiredServings && (
              <span style={{ fontSize: '0.85rem', color: '#ff6b6b', background: '#fff0f0', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                份量：{desiredServings} {recipe.servingUnit || '份'}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="cooking-progress-bar">
        <div className="cooking-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      <div className="cooking-content">
        {currentStep.imageUrl && (
          <div className="cooking-image-container">
            <img src={currentStep.imageUrl} alt={`Step ${currentStepIndex + 1}`} />
          </div>
        )}
        
        <div className="cooking-details-container">
          <p className="cooking-instruction">{currentStep.details}</p>
          
          {currentStep.ingredients && currentStep.ingredients.length > 0 && (
            <div className="cooking-ingredients-box">
              <h3>本步所需食材：</h3>
              <div className="cooking-ingredients-list">
                {currentStep.ingredients.map((ing, idx) => (
                  <div key={idx} className="cooking-ingredient-item">
                    <span className="ing-name">{ing.name}</span>
                    <span className="ing-weight">{getAdjustedWeightDisplay(ing.weight)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="cooking-footer">
        <button 
          className="nav-btn prev-btn" 
          onClick={handlePrev}
          disabled={currentStepIndex === 0}
        >
          <ChevronLeft size={36} />
          <span>上一步</span>
        </button>
        
        <div className="step-counter">
          {currentStepIndex + 1} / {allSteps.length}
        </div>

        <button 
          className="nav-btn next-btn" 
          onClick={handleNext}
          disabled={currentStepIndex === allSteps.length - 1}
        >
          <span>下一步</span>
          <ChevronRight size={36} />
        </button>
      </div>
    </div>
  );
};

export default CookingMode;
